/**
 * Demo data generator service
 * Creates realistic sample evaluations for demo and testing purposes
 */

import type {
  Evaluation,
  CriteriaGroup,
  Solution,
  Participant,
  CriteriaScore,
  ScoreValue,
} from '@/types/evaluation.types';
import {
  Importance,
  ParticipantType,
  InvitationStatus,
  EvaluationStatus,
} from '@/types/evaluation.types';
import { storageService } from '@/services/local-storage';
import { updateCriterionWeights } from '@/services/scoring';
import { isScoringLocked } from '@/services/evaluation-status';

/**
 * Generates a realistic HR evaluation comparing payroll systems
 */
function generateHRPayrollEvaluation(): Evaluation {
  const id = storageService.generateId();
  const now = new Date();
  const targetDate = new Date();
  targetDate.setDate(targetDate.getDate() + 30);

  const solutions: Solution[] = [
    {
      id: storageService.generateId(),
      solutionName: 'ADP Workforce Now',
      vendorName: 'ADP',
    },
    {
      id: storageService.generateId(),
      solutionName: 'Paylocity',
      vendorName: 'Paylocity',
    },
    {
      id: storageService.generateId(),
      solutionName: 'Paychex Flex',
      vendorName: 'Paychex',
    },
  ];

  const criteriaGroups: CriteriaGroup[] = [
    {
      id: storageService.generateId(),
      name: 'Core Payroll Features',
      weight: 35,
      groupWeight: 0.35,
      criteria: [
        {
          id: storageService.generateId(),
          title: 'Multi-state Payroll Processing',
          description: 'Ability to process payroll across multiple states with automatic tax compliance',
          importance: Importance.Critical,
          category: 'Compliance',
          helpfulHints: 'Check if system handles state-specific requirements automatically',
        },
        {
          id: storageService.generateId(),
          title: 'Direct Deposit & Payment Options',
          description: 'Support for direct deposit, pay cards, and paper checks',
          importance: Importance.High,
          category: 'Payment',
        },
        {
          id: storageService.generateId(),
          title: 'Tax Filing & Compliance',
          description: 'Automated tax filing for federal, state, and local taxes',
          importance: Importance.Critical,
          category: 'Compliance',
        },
        {
          id: storageService.generateId(),
          title: 'Year-End Processing',
          description: 'W-2 and 1099 generation and electronic filing',
          importance: Importance.High,
          category: 'Compliance',
        },
      ],
    },
    {
      id: storageService.generateId(),
      name: 'Employee Self-Service',
      weight: 25,
      groupWeight: 0.25,
      criteria: [
        {
          id: storageService.generateId(),
          title: 'Mobile App Quality',
          description: 'Quality and functionality of mobile apps for iOS and Android',
          importance: Importance.High,
          category: 'User Experience',
        },
        {
          id: storageService.generateId(),
          title: 'Payroll Transparency',
          description: 'Easy access to pay stubs, tax forms, and payment history',
          importance: Importance.Medium,
          category: 'User Experience',
        },
        {
          id: storageService.generateId(),
          title: 'Profile Management',
          description: 'Employee ability to update personal information, tax withholdings, and direct deposit',
          importance: Importance.High,
          category: 'User Experience',
        },
      ],
    },
    {
      id: storageService.generateId(),
      name: 'Benefits Administration',
      weight: 20,
      groupWeight: 0.20,
      criteria: [
        {
          id: storageService.generateId(),
          title: 'Benefits Enrollment',
          description: 'Integrated benefits enrollment and management',
          importance: Importance.High,
          category: 'Benefits',
        },
        {
          id: storageService.generateId(),
          title: 'Carrier Integration',
          description: 'Direct integration with benefits carriers',
          importance: Importance.Medium,
          category: 'Integration',
        },
      ],
    },
    {
      id: storageService.generateId(),
      name: 'Reporting & Analytics',
      weight: 20,
      groupWeight: 0.20,
      criteria: [
        {
          id: storageService.generateId(),
          title: 'Standard Reports',
          description: 'Comprehensive library of pre-built reports',
          importance: Importance.High,
          category: 'Reporting',
        },
        {
          id: storageService.generateId(),
          title: 'Custom Report Builder',
          description: 'Ability to create custom reports without IT assistance',
          importance: Importance.Medium,
          category: 'Reporting',
        },
      ],
    },
  ];

  const participants: Participant[] = [
    {
      id: storageService.generateId(),
      name: 'Sarah Johnson',
      email: 'sarah.johnson@company.com',
      phone: '555-0101',
      participantType: ParticipantType.Host,
      criteriaGroupAssignments: null,
      invitedDate: now,
      invitationExpirationDate: null,
      lastActivityDate: now,
      invitationStatus: InvitationStatus.Accepted,
    },
    {
      id: storageService.generateId(),
      name: 'Michael Chen',
      email: 'michael.chen@company.com',
      participantType: ParticipantType.Evaluator,
      criteriaGroupAssignments: [criteriaGroups[0].id, criteriaGroups[3].id],
      invitedDate: now,
      invitationExpirationDate: null,
      lastActivityDate: now,
      invitationStatus: InvitationStatus.Accepted,
    },
  ];

  // Update criterion weights for all groups based on importance
  const updatedCriteriaGroups = criteriaGroups.map(updateCriterionWeights);

  return {
    id,
    name: 'HR Payroll System Selection',
    summary: 'Our current payroll system lacks multi-state tax compliance and costs $15K/month for just 250 employees. As we expand to 8 new states this year, we need a scalable solution that automates compliance and reduces administrative overhead while supporting our growth to 500+ employees.',
    targetCompletionDate: targetDate,
    targetSolutions: solutions,
    criteriaGroups: updatedCriteriaGroups,
    participants,
    invitationExpirationDays: 7,
    status: EvaluationStatus.Organizing,
    statusChangedDate: now,
    closedReason: null,
    createdDate: now,
    modifiedDate: now,
  };
}

/**
 * Generates a realistic CRM evaluation
 */
function generateCRMEvaluation(): Evaluation {
  const id = storageService.generateId();
  const now = new Date();
  const targetDate = new Date();
  targetDate.setDate(targetDate.getDate() + 45);

  const solutions: Solution[] = [
    {
      id: storageService.generateId(),
      solutionName: 'Salesforce Sales Cloud',
      vendorName: 'Salesforce',
    },
    {
      id: storageService.generateId(),
      solutionName: 'HubSpot CRM',
      vendorName: 'HubSpot',
    },
    {
      id: storageService.generateId(),
      solutionName: 'Microsoft Dynamics 365',
      vendorName: 'Microsoft',
    },
    {
      id: storageService.generateId(),
      solutionName: 'Zoho CRM',
      vendorName: 'Zoho',
    },
  ];

  const criteriaGroups: CriteriaGroup[] = [
    {
      id: storageService.generateId(),
      name: 'Lead & Opportunity Management',
      weight: 30,
      groupWeight: 0.30,
      criteria: [
        {
          id: storageService.generateId(),
          title: 'Lead Capture & Routing',
          description: 'Automated lead capture from multiple sources with intelligent routing',
          importance: Importance.Critical,
          category: 'Lead Management',
        },
        {
          id: storageService.generateId(),
          title: 'Pipeline Visualization',
          description: 'Visual pipeline management with drag-and-drop functionality',
          importance: Importance.High,
          category: 'Sales Process',
        },
        {
          id: storageService.generateId(),
          title: 'Deal Scoring',
          description: 'AI-powered lead and opportunity scoring',
          importance: Importance.Medium,
          category: 'Intelligence',
        },
      ],
    },
    {
      id: storageService.generateId(),
      name: 'Contact & Account Management',
      weight: 25,
      groupWeight: 0.25,
      criteria: [
        {
          id: storageService.generateId(),
          title: '360-Degree Customer View',
          description: 'Comprehensive view of customer interactions and history',
          importance: Importance.High,
          category: 'Customer Intelligence',
        },
        {
          id: storageService.generateId(),
          title: 'Duplicate Detection',
          description: 'Automatic detection and merging of duplicate records',
          importance: Importance.Medium,
          category: 'Data Quality',
        },
      ],
    },
    {
      id: storageService.generateId(),
      name: 'Integrations',
      weight: 25,
      groupWeight: 0.25,
      criteria: [
        {
          id: storageService.generateId(),
          title: 'Email Integration',
          description: 'Native integration with Gmail and Outlook',
          importance: Importance.Critical,
          category: 'Integration',
        },
        {
          id: storageService.generateId(),
          title: 'Marketing Automation',
          description: 'Integration with marketing automation platforms',
          importance: Importance.High,
          category: 'Integration',
        },
        {
          id: storageService.generateId(),
          title: 'API & Extensibility',
          description: 'Robust API for custom integrations',
          importance: Importance.High,
          category: 'Integration',
        },
      ],
    },
    {
      id: storageService.generateId(),
      name: 'Reporting & Analytics',
      weight: 20,
      groupWeight: 0.20,
      criteria: [
        {
          id: storageService.generateId(),
          title: 'Sales Dashboards',
          description: 'Real-time sales performance dashboards',
          importance: Importance.High,
          category: 'Analytics',
        },
        {
          id: storageService.generateId(),
          title: 'Forecasting',
          description: 'Sales forecasting with AI-powered insights',
          importance: Importance.Medium,
          category: 'Analytics',
        },
      ],
    },
  ];

  const participants: Participant[] = [
    {
      id: storageService.generateId(),
      name: 'Jessica Martinez',
      email: 'jessica.martinez@company.com',
      participantType: ParticipantType.Host,
      criteriaGroupAssignments: null,
      invitedDate: now,
      invitationExpirationDate: null,
      lastActivityDate: now,
      invitationStatus: InvitationStatus.Accepted,
    },
  ];

  // Update criterion weights for all groups based on importance
  const updatedCriteriaGroups = criteriaGroups.map(updateCriterionWeights);

  return {
    id,
    name: 'Sales CRM Platform Selection',
    summary: 'Our sales team is managing customer data across spreadsheets, email, and sticky notes, resulting in a 32% lead leakage rate and poor visibility into our pipeline. We need a modern CRM to centralize customer interactions, improve forecast accuracy, and scale with our planned expansion from 12 to 35 sales reps.',
    targetCompletionDate: targetDate,
    targetSolutions: solutions,
    criteriaGroups: updatedCriteriaGroups,
    participants,
    invitationExpirationDays: 7,
    status: EvaluationStatus.Organizing,
    statusChangedDate: now,
    closedReason: null,
    createdDate: now,
    modifiedDate: now,
  };
}

/**
 * Generates a realistic database technology evaluation
 */
function generateDatabaseEvaluation(): Evaluation {
  const id = storageService.generateId();
  const now = new Date();
  const targetDate = new Date();
  targetDate.setDate(targetDate.getDate() + 21);

  const solutions: Solution[] = [
    {
      id: storageService.generateId(),
      solutionName: 'PostgreSQL',
      vendorName: 'PostgreSQL Global Development Group',
    },
    {
      id: storageService.generateId(),
      solutionName: 'MongoDB',
      vendorName: 'MongoDB, Inc.',
    },
    {
      id: storageService.generateId(),
      solutionName: 'Amazon Aurora',
      vendorName: 'AWS',
    },
  ];

  const criteriaGroups: CriteriaGroup[] = [
    {
      id: storageService.generateId(),
      name: 'Performance & Scalability',
      weight: 35,
      groupWeight: 0.35,
      criteria: [
        {
          id: storageService.generateId(),
          title: 'Read/Write Throughput',
          description: 'Performance benchmarks for high-volume read and write operations',
          importance: Importance.Critical,
          category: 'Performance',
          helpfulHints: 'Test with realistic data volumes matching production workloads',
        },
        {
          id: storageService.generateId(),
          title: 'Horizontal Scalability',
          description: 'Ability to scale out across multiple nodes',
          importance: Importance.High,
          category: 'Scalability',
        },
        {
          id: storageService.generateId(),
          title: 'Connection Pooling',
          description: 'Efficient connection management for high-concurrency applications',
          importance: Importance.Medium,
          category: 'Performance',
        },
      ],
    },
    {
      id: storageService.generateId(),
      name: 'Data Model & Query Capabilities',
      weight: 25,
      groupWeight: 0.25,
      criteria: [
        {
          id: storageService.generateId(),
          title: 'Schema Flexibility',
          description: 'Support for evolving data models and schema migrations',
          importance: Importance.High,
          category: 'Data Model',
        },
        {
          id: storageService.generateId(),
          title: 'Complex Query Support',
          description: 'Advanced query capabilities (joins, aggregations, window functions)',
          importance: Importance.High,
          category: 'Query',
        },
        {
          id: storageService.generateId(),
          title: 'JSON Support',
          description: 'Native JSON data type and query capabilities',
          importance: Importance.Medium,
          category: 'Data Model',
        },
      ],
    },
    {
      id: storageService.generateId(),
      name: 'Operations & Maintenance',
      weight: 25,
      groupWeight: 0.25,
      criteria: [
        {
          id: storageService.generateId(),
          title: 'Backup & Recovery',
          description: 'Point-in-time recovery and automated backup capabilities',
          importance: Importance.Critical,
          category: 'Operations',
        },
        {
          id: storageService.generateId(),
          title: 'Monitoring & Observability',
          description: 'Built-in monitoring tools and metrics',
          importance: Importance.High,
          category: 'Operations',
        },
        {
          id: storageService.generateId(),
          title: 'Managed Service Available',
          description: 'Availability of fully managed cloud service option',
          importance: Importance.Medium,
          category: 'Operations',
        },
      ],
    },
    {
      id: storageService.generateId(),
      name: 'Developer Experience',
      weight: 15,
      groupWeight: 0.15,
      criteria: [
        {
          id: storageService.generateId(),
          title: 'ORM Support',
          description: 'Quality of support for popular ORMs (TypeORM, Prisma, etc.)',
          importance: Importance.High,
          category: 'Developer Tools',
        },
        {
          id: storageService.generateId(),
          title: 'Documentation Quality',
          description: 'Comprehensive and up-to-date documentation',
          importance: Importance.Medium,
          category: 'Developer Tools',
        },
      ],
    },
  ];

  const participants: Participant[] = [
    {
      id: storageService.generateId(),
      name: 'David Kim',
      email: 'david.kim@company.com',
      participantType: ParticipantType.Host,
      criteriaGroupAssignments: null,
      invitedDate: now,
      invitationExpirationDate: null,
      lastActivityDate: now,
      invitationStatus: InvitationStatus.Accepted,
    },
    {
      id: storageService.generateId(),
      name: 'Priya Patel',
      email: 'priya.patel@company.com',
      participantType: ParticipantType.Evaluator,
      criteriaGroupAssignments: [criteriaGroups[0].id, criteriaGroups[1].id],
      invitedDate: now,
      invitationExpirationDate: null,
      lastActivityDate: now,
      invitationStatus: InvitationStatus.Accepted,
    },
  ];

  // Update criterion weights for all groups based on importance
  const updatedCriteriaGroups = criteriaGroups.map(updateCriterionWeights);

  return {
    id,
    name: 'Database Technology Selection',
    summary: 'We\'re transitioning from a monolithic architecture to microservices and our current MySQL setup can\'t handle the distributed data access patterns. We need a database solution that supports high-volume concurrent reads/writes, provides horizontal scalability, and aligns with our cloud-native infrastructure strategy.',
    targetCompletionDate: targetDate,
    targetSolutions: solutions,
    criteriaGroups: updatedCriteriaGroups,
    participants,
    invitationExpirationDays: 7,
    status: EvaluationStatus.Organizing,
    statusChangedDate: now,
    closedReason: null,
    createdDate: now,
    modifiedDate: now,
  };
}

/**
 * Generates a realistic cloud platform evaluation (with AWS as standard)
 */
function generateCloudPlatformEvaluation(): Evaluation {
  const id = storageService.generateId();
  const now = new Date();
  const targetDate = new Date();
  targetDate.setDate(targetDate.getDate() + 60);

  const solutions: Solution[] = [
    {
      id: storageService.generateId(),
      solutionName: 'AWS (Current Standard)',
      vendorName: 'Amazon Web Services',
    },
    {
      id: storageService.generateId(),
      solutionName: 'Google Cloud Platform',
      vendorName: 'Google',
    },
    {
      id: storageService.generateId(),
      solutionName: 'Microsoft Azure',
      vendorName: 'Microsoft',
    },
  ];

  const criteriaGroups: CriteriaGroup[] = [
    {
      id: storageService.generateId(),
      name: 'Service Availability',
      weight: 30,
      groupWeight: 0.30,
      criteria: [
        {
          id: storageService.generateId(),
          title: 'Compute Options',
          description: 'Range of compute services (VMs, containers, serverless)',
          importance: Importance.Critical,
          category: 'Core Services',
        },
        {
          id: storageService.generateId(),
          title: 'Database Services',
          description: 'Managed database offerings (SQL, NoSQL, caching)',
          importance: Importance.High,
          category: 'Core Services',
        },
        {
          id: storageService.generateId(),
          title: 'AI/ML Services',
          description: 'Machine learning and AI platform capabilities',
          importance: Importance.Medium,
          category: 'Advanced Services',
        },
      ],
    },
    {
      id: storageService.generateId(),
      name: 'Cost & Economics',
      weight: 25,
      groupWeight: 0.25,
      criteria: [
        {
          id: storageService.generateId(),
          title: 'Pricing Transparency',
          description: 'Clarity and predictability of pricing model',
          importance: Importance.High,
          category: 'Cost',
        },
        {
          id: storageService.generateId(),
          title: 'Reserved Instance Discounts',
          description: 'Available discounts for long-term commitments',
          importance: Importance.High,
          category: 'Cost',
        },
        {
          id: storageService.generateId(),
          title: 'Cost Management Tools',
          description: 'Tools for monitoring and optimizing cloud spend',
          importance: Importance.Medium,
          category: 'Cost',
        },
      ],
    },
    {
      id: storageService.generateId(),
      name: 'Migration Effort',
      weight: 25,
      groupWeight: 0.25,
      criteria: [
        {
          id: storageService.generateId(),
          title: 'AWS Compatibility',
          description: 'Compatibility with existing AWS workloads and APIs',
          importance: Importance.Critical,
          category: 'Migration',
          helpfulHints: 'Consider migration costs and learning curve if switching from AWS',
        },
        {
          id: storageService.generateId(),
          title: 'Migration Tools',
          description: 'Available tools and services for migrating from AWS',
          importance: Importance.High,
          category: 'Migration',
        },
        {
          id: storageService.generateId(),
          title: 'Team Expertise',
          description: 'Alignment with existing team skills and experience',
          importance: Importance.High,
          category: 'Migration',
        },
      ],
    },
    {
      id: storageService.generateId(),
      name: 'Enterprise Support',
      weight: 20,
      groupWeight: 0.20,
      criteria: [
        {
          id: storageService.generateId(),
          title: 'Support Plans',
          description: 'Quality and availability of enterprise support options',
          importance: Importance.High,
          category: 'Support',
        },
        {
          id: storageService.generateId(),
          title: 'SLA Guarantees',
          description: 'Service level agreements and uptime guarantees',
          importance: Importance.High,
          category: 'Support',
        },
      ],
    },
  ];

  const participants: Participant[] = [
    {
      id: storageService.generateId(),
      name: 'Robert Taylor',
      email: 'robert.taylor@company.com',
      participantType: ParticipantType.Host,
      criteriaGroupAssignments: null,
      invitedDate: now,
      invitationExpirationDate: null,
      lastActivityDate: now,
      invitationStatus: InvitationStatus.Accepted,
    },
  ];

  // Update criterion weights for all groups based on importance
  const updatedCriteriaGroups = criteriaGroups.map(updateCriterionWeights);

  return {
    id,
    name: 'Cloud Platform Strategy Review',
    summary: 'Following our acquisition of TechCorp (Azure-based) and recent 40% cloud cost increase on AWS, we need to evaluate whether to migrate everything to AWS, embrace a multi-cloud strategy, or consolidate on a different provider. Decision must balance migration costs, team expertise, vendor lock-in risks, and long-term TCO.',
    targetCompletionDate: targetDate,
    targetSolutions: solutions,
    criteriaGroups: updatedCriteriaGroups,
    participants,
    invitationExpirationDays: 7,
    status: EvaluationStatus.Organizing,
    statusChangedDate: now,
    closedReason: null,
    createdDate: now,
    modifiedDate: now,
  };
}

/**
 * Generates a realistic Customer Data Platform evaluation
 */
function generateCDPEvaluation(): Evaluation {
  const id = storageService.generateId();
  const now = new Date();
  const targetDate = new Date();
  targetDate.setDate(targetDate.getDate() + 45);

  const solutions: Solution[] = [
    {
      id: storageService.generateId(),
      solutionName: 'Segment',
      vendorName: 'Twilio',
    },
    {
      id: storageService.generateId(),
      solutionName: 'mParticle',
      vendorName: 'mParticle',
    },
    {
      id: storageService.generateId(),
      solutionName: 'Rudderstack',
      vendorName: 'RudderStack',
    },
  ];

  const criteriaGroups: CriteriaGroup[] = [
    {
      id: storageService.generateId(),
      name: 'Data Collection & Integration',
      weight: 35,
      groupWeight: 0.35,
      criteria: [
        {
          id: storageService.generateId(),
          title: 'SDK Quality',
          description: 'Quality and ease of use of web and mobile SDKs',
          importance: Importance.Critical,
          category: 'Integration',
        },
        {
          id: storageService.generateId(),
          title: 'Source Integrations',
          description: 'Number and quality of pre-built source integrations',
          importance: Importance.High,
          category: 'Integration',
        },
        {
          id: storageService.generateId(),
          title: 'Real-time Data Processing',
          description: 'Latency for data collection and processing',
          importance: Importance.High,
          category: 'Performance',
        },
      ],
    },
    {
      id: storageService.generateId(),
      name: 'Destination Management',
      weight: 30,
      groupWeight: 0.30,
      criteria: [
        {
          id: storageService.generateId(),
          title: 'Destination Catalog',
          description: 'Number of supported marketing and analytics destinations',
          importance: Importance.Critical,
          category: 'Integration',
        },
        {
          id: storageService.generateId(),
          title: 'Data Warehouses',
          description: 'Support for streaming data to data warehouses (Snowflake, BigQuery, etc.)',
          importance: Importance.High,
          category: 'Integration',
        },
      ],
    },
    {
      id: storageService.generateId(),
      name: 'Data Quality & Governance',
      weight: 20,
      groupWeight: 0.20,
      criteria: [
        {
          id: storageService.generateId(),
          title: 'Data Validation',
          description: 'Schema validation and data quality checks',
          importance: Importance.High,
          category: 'Quality',
        },
        {
          id: storageService.generateId(),
          title: 'Privacy Controls',
          description: 'GDPR/CCPA compliance features and consent management',
          importance: Importance.Critical,
          category: 'Governance',
        },
      ],
    },
    {
      id: storageService.generateId(),
      name: 'Cost & Scalability',
      weight: 15,
      groupWeight: 0.15,
      criteria: [
        {
          id: storageService.generateId(),
          title: 'Pricing Model',
          description: 'Pricing based on events, MTUs, or other metrics',
          importance: Importance.High,
          category: 'Cost',
        },
        {
          id: storageService.generateId(),
          title: 'Event Volume Handling',
          description: 'Ability to handle high event volumes without degradation',
          importance: Importance.Medium,
          category: 'Performance',
        },
      ],
    },
  ];

  const participants: Participant[] = [
    {
      id: storageService.generateId(),
      name: 'Emily Anderson',
      email: 'emily.anderson@company.com',
      participantType: ParticipantType.Host,
      criteriaGroupAssignments: null,
      invitedDate: now,
      invitationExpirationDate: null,
      lastActivityDate: now,
      invitationStatus: InvitationStatus.Accepted,
    },
  ];

  // Update criterion weights for all groups based on importance
  const updatedCriteriaGroups = criteriaGroups.map(updateCriterionWeights);

  return {
    id,
    name: 'Customer Data Platform Selection',
    summary: 'Customer data is siloed across 12 different tools (GA4, Mixpanel, Salesforce, Stripe, etc.), making it impossible to create unified customer profiles or measure cross-channel attribution. Marketing is wasting 60% of their time on manual data exports. We need a CDP to centralize data collection, enable real-time personalization, and reduce our marketing tech stack complexity.',
    targetCompletionDate: targetDate,
    targetSolutions: solutions,
    criteriaGroups: updatedCriteriaGroups,
    participants,
    invitationExpirationDays: 7,
    status: EvaluationStatus.Organizing,
    statusChangedDate: now,
    closedReason: null,
    createdDate: now,
    modifiedDate: now,
  };
}

/**
 * Generates a realistic Customer Identity Access Management evaluation (with AWS Cognito as standard)
 */
function generateCIAMEvaluation(): Evaluation {
  const id = storageService.generateId();
  const now = new Date();
  const targetDate = new Date();
  targetDate.setDate(targetDate.getDate() + 30);

  const solutions: Solution[] = [
    {
      id: storageService.generateId(),
      solutionName: 'AWS Cognito (Current Standard)',
      vendorName: 'AWS',
    },
    {
      id: storageService.generateId(),
      solutionName: 'Auth0',
      vendorName: 'Okta',
    },
    {
      id: storageService.generateId(),
      solutionName: 'Firebase Authentication',
      vendorName: 'Google',
    },
    {
      id: storageService.generateId(),
      solutionName: 'Clerk',
      vendorName: 'Clerk',
    },
  ];

  const criteriaGroups: CriteriaGroup[] = [
    {
      id: storageService.generateId(),
      name: 'Authentication Methods',
      weight: 30,
      groupWeight: 0.30,
      criteria: [
        {
          id: storageService.generateId(),
          title: 'Social Login Providers',
          description: 'Support for Google, Apple, Facebook, GitHub, etc.',
          importance: Importance.Critical,
          category: 'Authentication',
        },
        {
          id: storageService.generateId(),
          title: 'Passwordless Authentication',
          description: 'Magic links, SMS OTP, and biometric authentication',
          importance: Importance.High,
          category: 'Authentication',
        },
        {
          id: storageService.generateId(),
          title: 'Multi-Factor Authentication',
          description: 'Built-in MFA with SMS, TOTP, and push notifications',
          importance: Importance.Critical,
          category: 'Security',
        },
      ],
    },
    {
      id: storageService.generateId(),
      name: 'Developer Experience',
      weight: 25,
      groupWeight: 0.25,
      criteria: [
        {
          id: storageService.generateId(),
          title: 'SDK Quality',
          description: 'Quality of SDKs for web, mobile, and backend',
          importance: Importance.High,
          category: 'Developer Tools',
        },
        {
          id: storageService.generateId(),
          title: 'Customization Flexibility',
          description: 'Ability to customize UI, flows, and branding',
          importance: Importance.High,
          category: 'Developer Tools',
        },
        {
          id: storageService.generateId(),
          title: 'Documentation & Examples',
          description: 'Quality and completeness of documentation',
          importance: Importance.Medium,
          category: 'Developer Tools',
        },
      ],
    },
    {
      id: storageService.generateId(),
      name: 'Security & Compliance',
      weight: 25,
      groupWeight: 0.25,
      criteria: [
        {
          id: storageService.generateId(),
          title: 'Compliance Certifications',
          description: 'SOC 2, HIPAA, GDPR, and other compliance standards',
          importance: Importance.Critical,
          category: 'Compliance',
        },
        {
          id: storageService.generateId(),
          title: 'Attack Protection',
          description: 'Bot detection, brute force protection, anomaly detection',
          importance: Importance.High,
          category: 'Security',
        },
      ],
    },
    {
      id: storageService.generateId(),
      name: 'Migration & AWS Integration',
      weight: 20,
      groupWeight: 0.20,
      criteria: [
        {
          id: storageService.generateId(),
          title: 'AWS Service Integration',
          description: 'Integration with other AWS services (API Gateway, Lambda, etc.)',
          importance: Importance.High,
          category: 'Integration',
          helpfulHints: 'Consider migration effort if moving away from AWS Cognito',
        },
        {
          id: storageService.generateId(),
          title: 'User Migration',
          description: 'Tools and processes for migrating existing user base',
          importance: Importance.High,
          category: 'Migration',
        },
        {
          id: storageService.generateId(),
          title: 'Cost Comparison',
          description: 'Total cost of ownership compared to current Cognito setup',
          importance: Importance.High,
          category: 'Cost',
        },
      ],
    },
  ];

  const participants: Participant[] = [
    {
      id: storageService.generateId(),
      name: 'Alex Thompson',
      email: 'alex.thompson@company.com',
      participantType: ParticipantType.Host,
      criteriaGroupAssignments: null,
      invitedDate: now,
      invitationExpirationDate: null,
      lastActivityDate: now,
      invitationStatus: InvitationStatus.Accepted,
    },
    {
      id: storageService.generateId(),
      name: 'Linda Rodriguez',
      email: 'linda.rodriguez@company.com',
      participantType: ParticipantType.Evaluator,
      criteriaGroupAssignments: [criteriaGroups[2].id],
      invitedDate: now,
      invitationExpirationDate: null,
      lastActivityDate: now,
      invitationStatus: InvitationStatus.Accepted,
    },
  ];

  // Update criterion weights for all groups based on importance
  const updatedCriteriaGroups = criteriaGroups.map(updateCriterionWeights);

  return {
    id,
    name: 'Customer Identity Platform Evaluation',
    summary: 'AWS Cognito is limiting our ability to ship new authentication features quickly, with no support for passkeys, limited social providers, and clunky customization. Our engineering team spends 2-3 sprints per quarter building workarounds. We need to evaluate whether a specialized CIAM platform would accelerate development, improve user experience, and reduce authentication-related support tickets (currently 18% of all tickets).',
    targetCompletionDate: targetDate,
    targetSolutions: solutions,
    criteriaGroups: updatedCriteriaGroups,
    participants,
    invitationExpirationDays: 7,
    status: EvaluationStatus.Organizing,
    statusChangedDate: now,
    closedReason: null,
    createdDate: now,
    modifiedDate: now,
  };
}

/**
 * Generates a random score with weighted distribution favoring 3-4 ratings
 * Distribution: 50% score 4, 30% score 3, 10% score 5, 5% score 2, 3% score 1, 2% score 0
 */
function generateRandomScore(): ScoreValue {
  const rand = Math.random() * 100;

  if (rand < 50) return 4;        // 50% - Meets
  if (rand < 80) return 3;        // 30% - Partially meets
  if (rand < 90) return 5;        // 10% - Exceeds
  if (rand < 95) return 2;        // 5% - Does not meet
  if (rand < 98) return 1;        // 3% - Not enough info
  return 0;                       // 2% - Skip
}

/**
 * Generates demo scorecards for selected participants in an evaluation
 *
 * @param evaluation - The evaluation to generate scorecards for
 * @param participantIds - Array of participant IDs to generate scorecards for
 * @param mode - 'empty' generates scorecards with null scores, 'random' generates realistic random scores
 * @returns Number of scorecards generated (participantIds.length × solutions.length)
 *
 * @remarks
 * - Subsequent runs overwrite existing scorecards for the same participant/solution pairs
 * - Random scores use weighted distribution: mostly 3-4 with some outliers
 * - Empty scorecards set all scores to null (not yet scored)
 * - Each participant gets a scorecard for every solution in the evaluation
 * - Respects participant's criteriaGroupAssignments (only scores assigned groups)
 * - Only generates scorecards if evaluation status allows scoring (not Concluded or Closed)
 */
export function generateDemoScorecards(
  evaluation: Evaluation,
  participantIds: string[],
  mode: 'empty' | 'random'
): number {
  // Don't generate scorecards if scoring is locked by evaluation status
  if (isScoringLocked(evaluation.status)) {
    console.warn(`Cannot generate scorecards: Evaluation "${evaluation.name}" is ${evaluation.status} and scoring is locked.`);
    return 0;
  }

  // Don't generate scorecards if evaluation is still in Organizing status
  if (evaluation.status === EvaluationStatus.Organizing) {
    console.warn(`Cannot generate scorecards: Evaluation "${evaluation.name}" is still in Organizing status and has not been activated.`);
    return 0;
  }

  let generatedCount = 0;

  // Loop through each selected participant
  for (const participantId of participantIds) {
    const participant = evaluation.participants.find(p => p.id === participantId);
    if (!participant) continue;

    // Determine which criteria groups this participant can score
    const assignedGroupIds = participant.criteriaGroupAssignments;
    let applicableGroups: CriteriaGroup[];

    if (assignedGroupIds === null) {
      // null means all groups are assigned (typical for Host)
      applicableGroups = evaluation.criteriaGroups;
    } else if (assignedGroupIds.length === 0) {
      // Empty array means no groups assigned (Viewer - should not score)
      applicableGroups = [];
    } else {
      // Only assigned groups
      applicableGroups = evaluation.criteriaGroups.filter((group) =>
        assignedGroupIds.includes(group.id)
      );
    }

    // Get all criteria IDs that this participant should score
    const applicableCriteriaIds = new Set(
      applicableGroups.flatMap((group) => group.criteria.map((c) => c.id))
    );

    // Loop through each solution
    for (const solution of evaluation.targetSolutions) {
      // Build scores array for all applicable criteria
      const scores: CriteriaScore[] = Array.from(applicableCriteriaIds).map((criteriaId) => ({
        criteriaId,
        score: mode === 'random' ? generateRandomScore() : null,
      }));

      // Save the scorecard (this will overwrite if one already exists)
      storageService.saveScores(
        evaluation.id,
        solution.id,
        scores,
        participant.id,
        participant.name
      );

      generatedCount++;
    }
  }

  return generatedCount;
}

/**
 * Generates all demo evaluations and saves them to storage
 */
export function generateDemoEvaluations(): number {
  const evaluations: Evaluation[] = [
    generateHRPayrollEvaluation(),
    generateCRMEvaluation(),
    generateDatabaseEvaluation(),
    generateCloudPlatformEvaluation(),
    generateCDPEvaluation(),
    generateCIAMEvaluation(),
  ];

  evaluations.forEach((evaluation) => {
    storageService.saveEvaluation(evaluation);
  });

  return evaluations.length;
}

/**
 * Clears all evaluations, templates, and scorecards from storage
 */
export function clearAllData(): void {
  // Clear localStorage keys
  localStorage.removeItem('evaluations');
  localStorage.removeItem('template');
  localStorage.removeItem('scorecards');

  console.log('All evaluation data cleared');
}
